import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '../context/AuthContext';
import getErrorMessage from '../utils/errorHandler';
import DemoPicks from '../components/DemoPicks';
import logo from '../Logos/LogoNoText cropped.png';
import '../styles/app/auth.css';
import { HIDE_MONEY } from '../utils/platform';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotMessage, setShowForgotMessage] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get('redirect') || '/dashboard';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate(redirectUrl);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pnp-auth">
      <div className="pnp-auth-stage" aria-hidden="true">
        <span className="pnp-auth-beam" />
        <span className="pnp-auth-orb pnp-auth-orb-red" />
        <span className="pnp-auth-orb pnp-auth-orb-blue" />
        <svg className="pnp-auth-cage pnp-auth-cage-1" viewBox="0 0 100 100"><path d="M29.3 1h41.4L99 29.3v41.4L70.7 99H29.3L1 70.7V29.3z" /></svg>
        <svg className="pnp-auth-cage pnp-auth-cage-2" viewBox="0 0 100 100"><path d="M29.3 1h41.4L99 29.3v41.4L70.7 99H29.3L1 70.7V29.3z" /></svg>
        <svg className="pnp-auth-cage pnp-auth-cage-3" viewBox="0 0 100 100"><path d="M29.3 1h41.4L99 29.3v41.4L70.7 99H29.3L1 70.7V29.3z" /></svg>
        <span className="pnp-claw pnp-auth-claw" />
      </div>

      <div className="pnp-auth-grid">
        <header className="pnp-auth-brand pnp-rise">
          <div className="pnp-auth-logo-wrap">
            <img className="pnp-auth-logo" src={logo} alt="Pick 'N Pray" />
          </div>
          <p className="pnp-eyebrow pnp-auth-eyebrow">Fight night pick'em</p>
          <h1 className="pnp-auth-title">
            Welcome <span className="pnp-auth-break pnp-punch">back</span>
          </h1>
          <p className="pnp-auth-lede">Sign in to Pick 'N Pray</p>
          <ul className="pnp-auth-points">
            <li>Pick every fight on the card</li>
            <li>Watch the leaderboard move as results land</li>
            <li>Talk trash with your pool in Corner Chat</li>
          </ul>
        </header>

        <section className="pnp-auth-card pnp-rise" style={{ '--i': 1 }}>
          <div className="pnp-auth-card-head">
            <p className="pnp-eyebrow pnp-eyebrow-mute">Your account</p>
            <h2 className="pnp-auth-card-title">Sign in</h2>
          </div>

          {error && (
            <div className="message message-error" role="alert">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label" htmlFor="login-email">Email</label>
              <input
                id="login-email"
                type="email"
                className="form-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="login-password">Password</label>
              <div className="pnp-auth-field">
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  className="form-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  className="pnp-auth-eye"
                  onClick={() => setShowPassword(!showPassword)}
                  title={showPassword ? 'Hide password' : 'Show password'}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                      <line x1="1" y1="1" x2="23" y2="23"></line>
                    </svg>
                  )}
                </button>
              </div>
              <button
                type="button"
                className="pnp-auth-forgot"
                onClick={() => setShowForgotMessage(true)}
              >
                Forgot password?
              </button>
              {showForgotMessage && (
                <p className="pnp-auth-note">
                  {HIDE_MONEY
                    ? "Bruh, this was started as a fun project and now we have users... I'll get an email service for this soon, but in the meantime you're gonna have to create a new account and ask the commish for a fresh invite. Sorry - we'll get it together soon!"
                    : "Bruh, this was started as a fun project and now we have users... I'll get an email service for this soon, but in the meantime you're gonna have to create a new account and tell the commish to toggle paid if you've paid. Sorry - we'll get it together soon!"}
                </p>
              )}
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-lg btn-block pnp-auth-submit"
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
              {!loading && (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              )}
            </button>
          </form>

          <p className="pnp-auth-switch">
            Don't have an account?{' '}
            <Link to={redirectUrl !== '/dashboard' ? `/register?redirect=${encodeURIComponent(redirectUrl)}` : '/register'}>
              Create one
            </Link>
          </p>

          {/* Native app has no marketing homepage to show this on, so give
              first-time users a way to try picks before signing up. */}
          {Capacitor.isNativePlatform() && !showDemo && (
            <p className="pnp-auth-demo-cta">
              <button
                type="button"
                className="pnp-auth-demo-btn"
                onClick={() => setShowDemo(true)}
              >
                New here? Try a demo pick
              </button>
            </p>
          )}
        </section>
      </div>

      {showDemo && (
        <div className="pnp-auth-demo">
          <DemoPicks />
        </div>
      )}
    </div>
  );
}

export default Login;
